"use client";

import { useEffect, useState } from "react";
import { getAccessibleProjects, getDocumentParserSettings, searchDocumentAction } from "./actions";
import { getAllDisplayMapping } from "@/backend2/services/project/parser-configuration";
import { ParserAttributeDisplayMapping } from "@/backend2/models/project";
import { searchStateUtils } from "@/utils/searchStateUtils";
import SearchResultItem from "@/components/search/SearchResultItem";

interface SearchResult {
    projectCode: string;
    resourceGuid: string;
    project: string;
    originator: string;
    volume: string;
    level: string;
    documentType: string;
    role: string;
    classification: string;
    documentNumber: number;
    suitabilityStatus: string;
    revision: string;
    fileName: string | null;
    fileExtension: string | null;
    folderPath: string | null;
    navigationPaths: SearchResultNavigationPath[];
}

interface SearchResultNavigationPath {
    role: string;
    path: string;
    highlight: {
        originator: boolean;
        volume: boolean;
        level: boolean;
        documentType: boolean;
        role: boolean;
        classification: boolean;
    }
}

interface SearchFormData {
    project: string;
    originator: string;
    volume: string;
    level: string;
    documentType: string;
    role: string;
    classification: string;
    description: string;
}

interface ClientSearchPageWrapperProps {
    projectCode: string;
    onNavigateToResource?: () => void;
}

export default function ClientSearchPageWrapper({ projectCode, onNavigateToResource }: ClientSearchPageWrapperProps) {
    const [isInitialized, setIsInitialized] = useState(false);
    const [formData, setFormData] = useState<SearchFormData>(searchStateUtils.getDefaultFormData(projectCode));
    const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
    const [filteredResults, setFilteredResults] = useState<SearchResult[]>([]);
    const [isFormValid, setIsFormValid] = useState(false);
    const [projectList, setProjectList] = useState<{ key: string, display: string }[]>([]);
    const [originatorList, setOriginatorList] = useState<{ key: string, display: string }[]>([]);
    const [volumeList, setVolumeList] = useState<{ key: string, display: string }[]>([]);
    const [levelList, setLevelList] = useState<{ key: string, display: string }[]>([]);
    const [documentTypeList, setDocumentTypeList] = useState<{ key: string, display: string }[]>([]);
    const [roleList, setRoleList] = useState<{ key: string, display: string }[]>([]);
    const [suitabilityStatusList, setSuitabilityStatusList] = useState<string[]>([]);
    const [classificationList, setClassificationList] = useState<{ key: string, display: string }[]>([]);
    const [isSearching, setIsSearching] = useState(false);
    const [displayMapping, setDisplayMapping] = useState<Record<string, Record<ParserAttributeDisplayMapping, string>>>({});

    useEffect(() => {
        sessionStorage.removeItem('selectedElementTypeId');

        const fetchDisplayMapping = async () => {
            setDisplayMapping(await getAllDisplayMapping());
        }
        fetchDisplayMapping();
    }, []);

    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            project: projectCode
        }));
    }, [projectCode, projectList]);

    useEffect(() => {
        // Check if at least one field has a value other than "All"
        const hasValue = Object.values(formData).some(value => value !== "All");
        setIsFormValid(hasValue);
    }, [formData]);

    useEffect(() => {
        setFilteredResults(searchResults);
    }, [searchResults]);

    // Save search state when form data or results change, but only after initialization
    useEffect(() => {
        // Don't save during initialization
        if (!isInitialized) return;
        
        // Only save if there's meaningful data (not just default form)
        const hasNonDefaultData = searchResults.length > 0 || 
                                  Object.values(formData).some(value => value !== "All" && value !== "");
        
        if (hasNonDefaultData) {
            searchStateUtils.saveSearchState(projectCode, formData, searchResults);
        }
    }, [formData, searchResults, projectCode, isInitialized]);

    useEffect(() => {
        const fetchDocumentParserSettings = async () => {
            const documentParserSettings = await getDocumentParserSettings(formData.project);
            if (documentParserSettings) {
                setOriginatorList(documentParserSettings.allowedOriginators);
                setVolumeList(documentParserSettings.allowedVolumes);
                setLevelList(documentParserSettings.allowedLevels);
                setDocumentTypeList(documentParserSettings.allowedDocumentTypes);
                setRoleList(documentParserSettings.allowedRoles);
                setClassificationList(documentParserSettings.allowedClassifications);
                setSuitabilityStatusList(documentParserSettings.allowedSuitabilityStatuses);
            }
        };
        fetchDocumentParserSettings();
    }, [formData.project]);

    useEffect(() => {
        const fetchProjects = async () => {
            const accessibleProjects = await getAccessibleProjects();
            setProjectList(accessibleProjects);
            
            // Always check for saved state first
            const savedState = searchStateUtils.loadSearchState(projectCode);
            if (savedState) {
                setFormData(savedState.formData);
                setSearchResults(savedState.searchResults);
            } else {
                setFormData({
                    project: projectCode,
                    originator: "All",
                    volume: "All",
                    level: "All",
                    documentType: "All",
                    role: "All",
                    classification: "All",
                    description: ""
                });
            }
            
            // Mark as initialized after loading
            setIsInitialized(true);
        };
        fetchProjects();
    }, [projectCode]);

    const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const { value } = e.target;
        setFormData({
            project: value,
            originator: "All",
            volume: "All",
            level: "All",
            documentType: "All",
            role: "All",
            classification: "All",
            description: ""
        });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        setIsSearching(true);
        setSearchResults([]);
        e.preventDefault();
        if (!isFormValid) return;

        const results = await searchDocumentAction(formData.project, suitabilityStatusList, formData);
        setSearchResults(results);
        setIsSearching(false);
    };

    const handleReset = () => {
        const defaultData = searchStateUtils.getDefaultFormData(projectCode);
        setFormData(defaultData);
        setSearchResults([]);
        searchStateUtils.clearSearchState(projectCode);
    };

    // Show loading indicator during initialization
    if (!isInitialized) {
        return (
            <div className="flex items-center justify-center p-12">
                <div className="flex flex-col items-center space-y-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Loading search...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex flex-col">
            <div className="p-6 flex-shrink-0">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex justify-between items-center">
                        <h1 className="text-xl font-bold">Search</h1>
                    </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    <div className="hidden">
                        <label className="block mb-1 text-xs">Project</label>
                        <select
                            name="project"
                            value={formData.project}
                            onChange={handleProjectChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            {projectList.map((project) => (
                                <option key={project.key} value={project.key}>{project.key} - {project.display}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block mb-1 text-xs">{displayMapping[formData.project]?.[ParserAttributeDisplayMapping.Originator]}</label>
                        <select
                            name="originator"
                            value={formData.originator}
                            onChange={handleInputChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            <option value="All">All</option>
                            {originatorList.map((originator) => (
                                <option key={originator.key} value={originator.key}>{originator.key} - {originator.display}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block mb-1 text-xs">{displayMapping[formData.project]?.[ParserAttributeDisplayMapping.Volume]}</label>
                        <select
                            name="volume"
                            value={formData.volume}
                            onChange={handleInputChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            <option value="All">All</option>
                            {volumeList.map((volume) => (
                                <option key={volume.key} value={volume.key}>{volume.key} - {volume.display}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block mb-1 text-xs">{displayMapping[formData.project]?.[ParserAttributeDisplayMapping.Level]}</label>
                        <select
                            name="level"
                            value={formData.level}
                            onChange={handleInputChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            <option value="All">All</option>
                            {levelList.map((level) => (
                                <option key={level.key} value={level.key}>{level.key} - {level.display}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block mb-1 text-xs">{displayMapping[formData.project]?.[ParserAttributeDisplayMapping.DocumentType]}</label>
                        <select
                            name="documentType"
                            value={formData.documentType}
                            onChange={handleInputChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            <option value="All">All</option>
                            {documentTypeList.map((documentType) => (
                                <option key={documentType.key} value={documentType.key}>{documentType.key} - {documentType.display}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block mb-1 text-xs">{displayMapping[formData.project]?.[ParserAttributeDisplayMapping.Role]}</label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleInputChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            <option value="All">All</option>
                            {roleList.map((role) => (
                                <option key={role.key} value={role.key}>{role.key} - {role.display}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block mb-1 text-xs">{displayMapping[formData.project]?.[ParserAttributeDisplayMapping.Classification]}</label>
                        <select
                            name="classification"
                            value={formData.classification}
                            onChange={handleInputChange}
                            className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                        >
                            <option value="All">All</option>
                            {classificationList.map((classification) => (
                                <option key={classification.key} value={classification.key}>{classification.key} - {classification.display}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div>
                    <label className="block mb-1 text-xs">Description</label>
                    <input
                        type="text"
                        name="description"
                        value={formData.description}
                        onChange={handleInputChange}
                        className="w-full border p-2 border-gray-200 rounded-md cursor-pointer dark:bg-gray-300 dark:text-gray-500"
                    />
                </div>
                <div className="flex justify-between items-center">
                    <div>
                        <button
                            type="button"
                            onClick={handleReset}
                            className="px-4 py-2 rounded bg-gray-500 text-white hover:bg-gray-600"
                        >
                            Reset
                        </button>
                    </div>
                    <div className="space-x-2">
                        <button
                            type="submit"
                            disabled={!isFormValid}
                            className={`px-4 py-2 rounded ${isFormValid
                                ? 'bg-blue-500 text-white hover:bg-blue-600'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                }`}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </form>
            </div>
            
            {isSearching && (
                <div className="flex justify-center items-center flex-1 p-10">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-gray-900 dark:border-white"></div>
                </div>
            )}
            
            {filteredResults.length > 0 && (
                <div className="px-6 pb-6">
                    <div className="pb-2">
                        <h2 className="text-base font-semibold">Results ({filteredResults.length})</h2>
                    </div>
                    <div className="space-y-3">
                        {filteredResults.map((result, index) => (
                            <SearchResultItem
                                key={index}
                                result={result}
                                onNavigateToResource={onNavigateToResource}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
